import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import apiClient from '../../lib/api-client'
import { toast } from '../../components/Toast/useToast'
import { useAuthStore } from '../../stores/authStore'
import { isDemoMode, getDemoToken } from '../../services/demoMode'

export type Tag = {
  id: number
  name: string
  dashboard: boolean
  hotspots_count: number
  contacts_count: number
  created_at: string
  updated_at: string
  hotspot_ids?: number[]
  contact_ids?: number[]
  hotspots?: Array<{ id: number; name: string; address: string }>
  contacts?: Array<{ id: number; name: string; email: string }>
}

function getAuthToken(): string | null {
  if (isDemoMode()) return getDemoToken()
  return useAuthStore.getState().accessToken ?? null
}

async function fetchJson(url: string, options: RequestInit) {
  const token = getAuthToken()
  const headers = new Headers(options.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  const response = await fetch(url, { ...options, headers })
  if (!response.ok) {
    const body = await response.text()
    throw new Error(body || `HTTP ${response.status}`)
  }
  return response.json()
}

export function useTags() {
  const { t } = useTranslation('tags')
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  return useQuery({
    queryKey: ['tags'],
    // Tags gehoeren immer einem Konto. Seit die Hotspot-Detailseite auch ohne
    // Anmeldung erreichbar ist (09.2026), wuerde diese Abfrage dort bei jedem
    // Aufruf in ein 401 laufen — ein Netzwerk-Aufruf, dessen Antwort von
    // vornherein feststeht.
    enabled: isAuthenticated,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v2/tags')
      if (error) throw new Error(t('toast.loadError'))
      return (data?.data ?? []) as Tag[]
    },
  })
}

export function useTag(id: number | string) {
  const { t } = useTranslation('tags')

  return useQuery({
    queryKey: ['tags', id],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v2/tags/{id}', {
        params: { path: { id: Number(id) } },
      })
      if (error) throw new Error(t('toast.loadError'))
      return data?.data as Tag
    },
    enabled: !!id,
  })
}

export function useCreateTag() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('tags')

  return useMutation({
    mutationFn: async (newTag: { name: string; dashboard?: boolean }) => {
      const { data, error } = await apiClient.POST('/api/v2/tags', {
        body: { tag: newTag },
      })
      if (error) throw new Error('TAG_CREATE_FAILED')
      return data?.data as Tag
    },
    onMutate: async (newTag) => {
      await queryClient.cancelQueries({ queryKey: ['tags'] })
      const previousTags = queryClient.getQueryData<Tag[]>(['tags'])

      const optimisticTag = {
        id: Math.random(),
        ...newTag,
        dashboard: newTag.dashboard ?? true,
        hotspots_count: 0,
        contacts_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as Tag

      queryClient.setQueryData<Tag[]>(['tags'], (old) => {
        return old ? [...old, optimisticTag] : [optimisticTag]
      })

      return { previousTags }
    },
    onError: (_err, _newTag, context) => {
      if (context?.previousTags) {
        queryClient.setQueryData(['tags'], context.previousTags)
      }
      toast.error(t('toast.createError'))
    },
    onSuccess: () => {
      toast.success(t('toast.created'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })
}

export function useUpdateTag() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('tags')

  return useMutation({
    mutationFn: async (updatedTag: { id: number; name: string; dashboard?: boolean }) => {
      const { data, error } = await apiClient.PATCH('/api/v2/tags/{id}', {
        params: { path: { id: updatedTag.id } },
        body: { tag: { name: updatedTag.name, dashboard: updatedTag.dashboard } },
      })
      if (error) throw new Error('TAG_UPDATE_FAILED')
      return data?.data as Tag
    },
    onMutate: async (updatedTag) => {
      await queryClient.cancelQueries({ queryKey: ['tags'] })
      const previousTags = queryClient.getQueryData<Tag[]>(['tags'])

      queryClient.setQueryData<Tag[]>(['tags'], (old) => {
        if (!old) return []
        return old.map(t => t.id === updatedTag.id ? { ...t, ...updatedTag } as Tag : t)
      })

      return { previousTags }
    },
    onError: (_err, _updatedTag, context) => {
      if (context?.previousTags) {
        queryClient.setQueryData(['tags'], context.previousTags)
      }
      toast.error(t('toast.updateError'))
    },
    onSuccess: () => {
      toast.success(t('toast.updated'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })
}

export function useDeleteTag() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('tags')

  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const { error } = await apiClient.DELETE('/api/v2/tags/{id}', {
        params: { path: { id } },
      })
      if (error) throw new Error('TAG_DELETE_FAILED')
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['tags'] })
      const previousTags = queryClient.getQueryData<Tag[]>(['tags'])

      queryClient.setQueryData<Tag[]>(['tags'], (old) => {
        if (!old) return []
        return old.filter(t => t.id !== id)
      })

      return { previousTags }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousTags) {
        queryClient.setQueryData(['tags'], context.previousTags)
      }
      toast.error(t('toast.deleteError'))
    },
    onSuccess: () => {
      toast.success(t('toast.deleted'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })
}

export function useAddHotspotsToTag() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('tags')

  return useMutation({
    mutationFn: async ({ tagId, hotspotIds }: { tagId: number; hotspotIds: number[] }) => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL
      return fetchJson(`${baseUrl}/api/v2/tags/${tagId}/add_hotspots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hotspot_ids: hotspotIds }),
      })
    },
    onSuccess: () => {
      toast.success(t('toast.hotspotsAdded'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['hotspots'] })
      // BUGFIX #44: das Tag-Dashboard (Stats, Widget-Werte, Fleet health,
      // Top-Hotspots) haengt an der Tag-Zuordnung und blieb nach einem
      // Tag-Wechsel auf dem alten Bestand stehen.
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // BUGFIX #43: der Detail-Query-Key traegt die Route-ID — das kann die
      // numerische ID ODER die Hotspot-Adresse sein. Ein gezieltes
      // ['hotspot', String(numericId)] verfehlt die Adress-Variante, die
      // Detailseite blieb dann auf dem alten Stand.
      queryClient.invalidateQueries({ queryKey: ['hotspot'] })
    },
  })
}

export function useRemoveHotspotsFromTag() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('tags')

  return useMutation({
    mutationFn: async ({ tagId, hotspotIds }: { tagId: number; hotspotIds: number[] }) => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL
      return fetchJson(`${baseUrl}/api/v2/tags/${tagId}/remove_hotspots`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hotspot_ids: hotspotIds }),
      })
    },
    onSuccess: () => {
      toast.success(t('toast.hotspotsRemoved'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['hotspots'] })
      // BUGFIX #44: das Tag-Dashboard (Stats, Widget-Werte, Fleet health,
      // Top-Hotspots) haengt an der Tag-Zuordnung und blieb nach einem
      // Tag-Wechsel auf dem alten Bestand stehen.
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // BUGFIX #43: der Detail-Query-Key traegt die Route-ID — das kann die
      // numerische ID ODER die Hotspot-Adresse sein. Ein gezieltes
      // ['hotspot', String(numericId)] verfehlt die Adress-Variante, die
      // Detailseite blieb dann auf dem alten Stand.
      queryClient.invalidateQueries({ queryKey: ['hotspot'] })
    },
  })
}

export function useAddContactsToTag() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('tags')

  return useMutation({
    mutationFn: async ({ tagId, contactIds }: { tagId: number; contactIds: number[] }) => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL
      return fetchJson(`${baseUrl}/api/v2/tags/${tagId}/add_contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_ids: contactIds }),
      })
    },
    onSuccess: () => {
      toast.success(t('toast.contactsAdded'))
    },
    onSettled: (_data, _err, { contactIds }) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      contactIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: ['contact-detail', String(id)] })
      })
    },
  })
}

export function useRemoveContactsFromTag() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('tags')

  return useMutation({
    mutationFn: async ({ tagId, contactIds }: { tagId: number; contactIds: number[] }) => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL
      return fetchJson(`${baseUrl}/api/v2/tags/${tagId}/remove_contacts`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_ids: contactIds }),
      })
    },
    onSuccess: () => {
      toast.success(t('toast.contactsRemoved'))
    },
    onSettled: (_data, _err, { contactIds }) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      contactIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: ['contact-detail', String(id)] })
      })
    },
  })
}
